/**
 * Public routes whose `<title>` the SERVER already wrote, and which the client
 * layout must therefore leave alone.
 *
 * THE PROBLEM THIS SOLVES. `(public)/layout.tsx` sets `document.title` on every
 * render from the page-title context, and when a page sets nothing it invents a
 * title from the last path segment — so `/programs/tdmk` became "Tdmk". That was
 * right when every public page was client-rendered. It is wrong the moment a
 * page exports `generateMetadata`: the server writes "TDMK Athletics — Houston,
 * TX", hydration overwrites it, and Googlebot — which does render — indexes the
 * overwritten one. Deleting the page's own `usePageTitle` call is not enough,
 * because the layout's fallback then takes over and produces something worse.
 *
 * MATCHED ON THE PATH, NOT SIGNALLED THROUGH CONTEXT. A server page cannot set
 * a client context value, and a client child that claimed it on mount would race
 * the layout's own effect — the layout reads the value it rendered with, so the
 * claim lands a render too late. The path is known to both, synchronously, with
 * no ordering to get wrong.
 *
 * ADD A ROUTE HERE IN THE SAME CHANGE THAT GIVES IT `generateMetadata`. A page
 * converted without this entry keeps its server title for one paint and then
 * loses it; a route listed here without a server title gets whatever the browser
 * defaults to. The suite pins both directions.
 */

/** `/programs/<gymSlug>` and nothing below it. */
const CATALOG_ROUTE = /^\/programs\/[^/]+\/?$/;

/**
 * `/programs/<gymSlug>/<programType>/<programId>` — a program's own page, on
 * either of the two routes that serve one.
 *
 * NOTHING BELOW IT. `/enroll` and `/book` sit one segment further down, they
 * are still client-rendered, and they still want the layout's title — the `$`
 * is what keeps them out.
 */
const PROGRAM_DETAIL_ROUTE = /^\/programs\/[^/]+\/[^/]+\/[^/]+\/?$/;

/**
 * `/programs/embed-preview` is a `[gymSlug]`-shaped path that is NOT the
 * catalog — it is the gym-website embed preview, still client-rendered, and it
 * still wants the layout's title.
 */
const NOT_A_GYM_SLUG = new Set(['embed-preview']);

export function ownsItsOwnDocumentTitle(pathname: string | null | undefined): boolean {
  if (typeof pathname !== 'string') return false;
  if (!CATALOG_ROUTE.test(pathname) && !PROGRAM_DETAIL_ROUTE.test(pathname)) return false;

  const segment = pathname.replace(/\/$/, '').split('/')[2];
  return !NOT_A_GYM_SLUG.has(segment);
}
